import type {
  BatMetadataConfigEntry,
  FieldMetadataMessageDescriptorDef,
} from './common/types';
import { coreFields } from './common/commonFields';
import { locationFields } from './common/locationFields';
import { vendorFields } from './common/vendorFields';
import { productFields } from './common/productFields';
import { msg } from '@lingui/core/macro';
import {
  fuelConsumptionUnitField,
  fuelKindField,
  lifetimeFuelConsumptionField,
} from './common/fuelFields';
import {
  vehicleCategoryField,
  vehiclePowertrainTypeField,
  vehicleTypeField,
} from './common/travelFields';
import {
  lifetimeElectricityConsumptionField,
  lifetimeElectricityConsumptionUnitField,
} from './common/electricityFields';

const vehicleQuantity: FieldMetadataMessageDescriptorDef = {
  displayName: msg({
    message: 'Vehicle quantity',
    context: 'The number of vehicles sold.',
  }),
  externalDescription: msg({
    message: 'The number of vehicles sold.',
    context: 'Vehicle quantity',
  }),
};

export const soldVehicleMetadata = {
  displayName: msg({
    message: 'Sold products: Vehicles',
    context: 'Activity data schema name for sold vehicles',
  }),
  fields: {
    ...coreFields,
    ...locationFields,
    ...vendorFields,
    ...productFields,
    fuel_consumed_time_of_sale: {
      displayName: msg({
        message: 'Fuel consumed at time of sale',
        context:
          'Name of a column of data describing an amount of fuel consumption',
      }),
      externalDescription: msg({
        message:
          'For used vehicles, fuel already consumed by the vehicle at time of sale.',
        context:
          'Description of a column of data describing an amount of fuel consumption',
      }),
    },
    fuel_consumption_unit: fuelConsumptionUnitField,
    fuel_kind: fuelKindField,
    lifetime_distance_traveled: {
      displayName: msg({
        message: 'Lifetime distance traveled',
        context:
          'Name of a column of data describing the distance a vehicle traveled',
      }),
      externalDescription: msg({
        message:
          'Distance the vehicle will approximately travel in its entire lifetime.',
        context:
          'Description of a column of data describing the distance a vehicle traveled',
      }),
    },
    lifetime_distance_traveled_unit: {
      displayName: msg({
        message: 'Lifetime distance traveled unit',
        context:
          'Unit (e.g. km, miles) of a column of data describing the distance a vehicle traveled',
      }),
      externalDescription: msg({
        message: 'The unit of lifetime distance traveled',
        context:
          'Description of a unit (e.g. km, miles) for a column of data describing the distance a vehicle traveled',
      }),
    },
    lifetime_electricity_consumption: {
      ...lifetimeElectricityConsumptionField,
      externalDescription: msg({
        message:
          'Electricity the vehicle will approximately consume in its entire lifetime.',
        context: 'Description for a column of data',
      }),
    },
    lifetime_electricity_consumption_unit:
      lifetimeElectricityConsumptionUnitField,
    lifetime_fuel_consumption: {
      ...lifetimeFuelConsumptionField,
      externalDescription: msg({
        message:
          'Fuel the vehicle will approximately consume in its entire lifetime.',
        context: 'Description for a column of data',
      }),
    },
    odometer_reading_time_of_sale: {
      displayName: msg({
        message: 'Odometer reading at time of sale',
        context: 'Name of a column of data describing vehicles that were sold',
      }),
      externalDescription: msg({
        message: 'Odometer reading of the vehicle at time of sale.',
        context:
          'Description fora column of data describing vehicles that were sold',
      }),
    },
    odometer_reading_time_of_sale_unit: {
      displayName: msg({
        message: 'Odometer reading unit',
        context:
          'Name of a column of data describing vehicles that were sold. Describes the unit of another column, e.g. miles.',
      }),
      externalDescription: msg({
        message: 'The unit of odometer reading at time of sale.',
        context:
          'Description for a column of data describing vehicles that were sold. Describes the unit of another column, e.g. miles.',
      }),
    },
    vehicle_category: vehicleCategoryField,
    vehicle_powertrain_type: vehiclePowertrainTypeField,
    vehicle_quantity: vehicleQuantity,
    // NOTE: quantity does not exist on the BAT but vehicle_quantity is
    // renamed to quantity in the BART-EM matching process
    quantity: vehicleQuantity,
    vehicle_type: vehicleTypeField,
  },
} as const satisfies BatMetadataConfigEntry;
